/**
 * Null-tolerant formatters used throughout the UI.
 *
 * These wrap the locale-aware formatters in `@/i18n` so that callers don't
 * have to null-check every date field from the API. The active locale
 * (pt-BR by default) and the project timezone (America/Sao_Paulo) are
 * applied automatically via the i18n module.
 *
 * Money:
 *   - The API stores amounts as integer cents (avoids float drift). This
 *     module divides by 100 before handing to the BRL formatter.
 */

import {
  formatDate as formatDateI18n,
  formatDateTime as formatDateTimeI18n,
  formatRelative as formatRelativeI18n,
  formatMoney as formatMoneyI18n,
  getLocale,
} from '@/i18n';

const EMPTY = '—';

export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return formatDateI18n(value);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return formatDateTimeI18n(value);
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return formatRelativeI18n(value);
}

/** Integer cents (BRL) → `R$ 1.234,56`. Nullable fields become an em-dash. */
export function formatMoney(cents: number | null | undefined): string {
  if (cents == null) return EMPTY;
  return formatMoneyI18n(cents / 100);
}

/**
 * Compact BRL formatter for KPI tiles where horizontal space is tight.
 *
 * Rules:
 *   - < R$ 1.000          → full string ("R$ 250,00")
 *   - R$ 1.000 – 99.999   → "R$ 1,2 mil"
 *   - R$ 100.000 – 999k   → "R$ 250 mil"
 *   - R$ 1.000.000+       → "R$ 1,5 mi"
 *   - R$ 1.000.000.000+   → "R$ 1,5 bi"
 *
 * pt-BR convention: comma as decimal separator, lowercase suffix, no
 * period. Always pair with a `title=` attribute carrying the full
 * `formatMoney` value so a hover/focus reveals the exact amount.
 */
export function formatMoneyCompact(cents: number | null | undefined): string {
  if (cents == null) return EMPTY;
  const reais = cents / 100;
  const abs = Math.abs(reais);
  const sign = reais < 0 ? '-' : '';
  if (abs < 1_000) {
    return formatMoneyI18n(reais);
  }
  const fmt = (n: number, suffix: string): string => {
    // 1 decimal when the abbreviated value is <10, otherwise integer.
    // "R$ 1,5 mi" reads cleaner than "R$ 1,50 mi" or "R$ 2 mi" for 1.5M.
    const decimals = n < 10 ? 1 : 0;
    return `${sign}R$ ${n
      .toFixed(decimals)
      .replace('.', ',')
      .replace(/,0$/, '')} ${suffix}`;
  };
  if (abs >= 1_000_000_000) return fmt(abs / 1_000_000_000, 'bi');
  if (abs >= 1_000_000) return fmt(abs / 1_000_000, 'mi');
  if (abs >= 100_000) return fmt(abs / 1_000, 'mil');
  return fmt(abs / 1_000, 'mil');
}

/**
 * Wave Bid-Hardening · 2026-05-11 · máscara BRL dummy-friendly.
 *
 * Recebe raw input ("11111", "1.234,56", "abc12") e devolve
 * `{ display, cents, error }`. Strategy:
 *   - extrai só digits, trata como centavos (últimas 2 = decimal)
 *   - "11111" → display "111,11" · cents 11111
 *   - max enforcement opcional (default R$ 99.999,99 = 9_999_999 cents)
 *
 * Usar em onChange de Input. Se `cents > maxCents`, ainda retorna o
 * valor antigo (não atualiza) + error string.
 */
export interface BrlMaskResult {
  display: string;
  cents: number;
  error?: string;
}

export function maskBrlInput(raw: string, opts?: { maxCents?: number }): BrlMaskResult {
  const maxCents = opts?.maxCents ?? 9_999_999; // R$ 99.999,99 default
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 12);
  if (!digits) return { display: '', cents: 0 };
  const cents = Number.parseInt(digits, 10);
  if (cents > maxCents) {
    const isEn = getLocale() === 'en';
    const max = (maxCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    return {
      display: '',
      cents: 0,
      error: isEn
        ? `Maximum R$ ${max} per unit · enter a smaller value`
        : `Valor máximo R$ ${max} por unidade · digite valor menor`,
    };
  }
  const reais = Math.floor(cents / 100);
  const dec = (cents % 100).toString().padStart(2, '0');
  const reaisFmt = reais.toLocaleString('pt-BR');
  return { display: `${reaisFmt},${dec}`, cents };
}

export function centsToBrlInput(cents: number | null | undefined): string {
  if (cents == null || cents <= 0) return '';
  const reais = Math.floor(cents / 100);
  const dec = (cents % 100).toString().padStart(2, '0');
  return `${reais.toLocaleString('pt-BR')},${dec}`;
}

/**
 * Parse a PT-BR formatted number string to a float.
 * Handles thousands separators (.) and decimal comma (,).
 *   "3.500,75" → 3500.75
 *   "1.200"    → 1200
 *   "150,5"    → 150.5
 */
export function parsePtBrFloat(s: string): number {
  return parseFloat(s.replace(/\./g, '').replace(',', '.'));
}
